import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { hrRequisitionApi } from "../api";
import { isExternalWorkflow } from "../types";
import type { HrRequisitionLine, HrRequisitionStatus } from "../types";

function useAsyncData<T>(fn: () => Promise<T>, deps: unknown[]) {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [rev, setRev] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    fn()
      .then((d) => { if (!cancelled) { setData(d); setLoading(false); } })
      .catch((e) => { if (!cancelled) { setError(String(e?.message ?? e)); setLoading(false); } });
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, rev]);

  return { data, loading, error, reload: () => setRev((r) => r + 1) };
}

// ── Status config ─────────────────────────────────────────────────────────────

// Staff Requisitions only ever run the internal (Dept Head → Store Officer) workflow — the
// external procurement stages (In Procurement, Quotations Received, Budget Approved, Evaluated)
// belong to Service/Works/General requisitions, which are no longer created here (2026-07-09,
// see the Procurement module instead). Matches the status set used in Requisition Reports.
const STATUS_TABS: { label: string; value: HrRequisitionStatus | "" }[] = [
  { label: "All",                   value: "" },
  { label: "Draft",                 value: "DRAFT" },
  { label: "Submitted",             value: "SUBMITTED" },
  { label: "Approved",              value: "APPROVED" },
  { label: "Fulfilled",             value: "FULFILLED" },
  { label: "Partial",               value: "PARTIAL" },
  { label: "Rejected",              value: "REJECTED" },
  { label: "Cancelled",             value: "CANCELLED" },
];

const STATUS_CLASSES: Record<string, string> = {
  DRAFT:                "bg-slate-100 text-slate-600",
  SUBMITTED:            "bg-amber-50 text-amber-700 border border-amber-200",
  PENDING_APPROVAL:     "bg-amber-50 text-amber-700 border border-amber-200",
  APPROVED:             "bg-emerald-100 text-emerald-800",
  FULFILLED:            "bg-blue-100 text-blue-700",
  PARTIAL:              "bg-sky-100 text-sky-700",
  PURCHASED:            "bg-blue-100 text-blue-700",
  CLOSED:               "bg-slate-200 text-slate-600",
  REJECTED:             "bg-red-100 text-red-700",
  CANCELLED:            "bg-red-50 text-red-500",
  QUOTATION_REQUESTED:  "bg-purple-100 text-purple-700",
  QUOTATIONS_RECEIVED:  "bg-indigo-100 text-indigo-700",
  BUDGET_APPROVED:      "bg-teal-100 text-teal-700",
  BUDGET_REJECTED:      "bg-red-100 text-red-700",
  EVALUATED:            "bg-orange-100 text-orange-700",
};

function StatusBadge({ value }: { value?: string | null }) {
  const cls = STATUS_CLASSES[value ?? ""] ?? "bg-slate-100 text-slate-600";
  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${cls}`}>
      {(value ?? "").replace(/_/g, " ")}
    </span>
  );
}

/** Shows the actual item(s) requested — first line's description (+ qty), with a "+N more"
 *  hint for multi-line requisitions — instead of the free-text purpose, so viewers can tell
 *  what was requested at a glance. */
function ItemsCell({ lines, pending }: { lines?: HrRequisitionLine[] | null; pending?: boolean }) {
  if (!lines || lines.length === 0) {
    return <span className="text-slate-300">{pending ? "…" : "—"}</span>;
  }
  const first = lines[0];
  const extra = lines.length - 1;
  return (
    <span>
      {first.description}
      {first.quantity ? ` (x${first.quantity}${first.unitOfMeasure ? ` ${first.unitOfMeasure}` : ""})` : ""}
      {extra > 0 && <span className="ml-1.5 text-slate-400">+{extra} more</span>}
    </span>
  );
}

function TypeBadge({ value }: { value?: string | null }) {
  if (!value) return null;
  const external = isExternalWorkflow(value);
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${external ? "bg-purple-50 text-purple-600" : "bg-emerald-50 text-emerald-700"}`}>
      {value.replace(/_/g, " ")}
    </span>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export default function RequisitionsPage() {
  const [status, setStatus] = useState<HrRequisitionStatus | "">("");
  const [page, setPage] = useState(0);

  // Pass status to the server — never filter client-side on a paginated list
  const { data, loading, error } = useAsyncData(
    () => hrRequisitionApi.mine({ page, size: 10 }),
    [page, status],
  );

  // The list endpoint (GET .../mine) doesn't populate `lines` — only the detail endpoint
  // (GET .../{id}) does. Page size is capped at 10, so fan out one detail fetch per row to
  // pull in item descriptions for the "Item(s) Requested" column, keyed by id and cached
  // across pages/status-tab switches so we don't refetch rows we've already seen.
  const [linesById, setLinesById] = useState<Record<number, HrRequisitionLine[]>>({});

  useEffect(() => {
    if (!data) return;
    const toFetch = data.content.filter(
      (r) => (!r.lines || r.lines.length === 0) && !linesById[r.id],
    );
    if (toFetch.length === 0) return;
    let cancelled = false;
    Promise.all(
      toFetch.map((r) =>
        hrRequisitionApi.get(r.id).then(
          (full): [number, HrRequisitionLine[]] => [r.id, full.lines ?? []],
          (): [number, HrRequisitionLine[]] => [r.id, []],
        ),
      ),
    ).then((pairs) => {
      if (cancelled) return;
      setLinesById((prev) => {
        const next = { ...prev };
        for (const [id, lines] of pairs) next[id] = lines;
        return next;
      });
    });
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  // Apply status filter client-side only as a lightweight UX aid when data is
  // already loaded (the server endpoint currently doesn't accept a status param
  // — if it does in future, pass it directly and remove this filter)
  const rows = data
    ? status
      ? data.content.filter((r) => r.status === status)
      : data.content
    : [];

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">My Staff Requisitions</h1>
        </div>
        <Link
          to="/hr/requisitions/new"
          className="rounded-xl bg-emerald-700 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-800"
        >
          + New Requisition
        </Link>
      </div>

      {/* Status tabs */}
      <div className="flex flex-wrap gap-1 rounded-2xl border bg-slate-50 p-1">
        {STATUS_TABS.map((t) => (
          <button
            key={t.value}
            type="button"
            onClick={() => { setStatus(t.value); setPage(0); }}
            className={[
              "rounded-xl px-3 py-1.5 text-xs font-semibold transition-all",
              status === t.value
                ? "bg-emerald-700 text-white shadow-sm"
                : "text-slate-600 hover:bg-white hover:text-slate-900",
            ].join(" ")}
          >
            {t.label}
          </button>
        ))}
      </div>

      {loading && <div className="rounded-xl border bg-white p-5 text-sm text-slate-500">Loading…</div>}
      {error && (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          <strong>Error:</strong> {error}
        </div>
      )}

      {!loading && !error && data && (
        <>
          <div className="rounded-2xl border bg-white shadow-sm overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="bg-slate-50 text-xs uppercase text-slate-500">
                <tr>
                  <th className="px-4 py-3">Serial No</th>
                  <th className="px-4 py-3">Type</th>
                  <th className="px-4 py-3">Item(s) Requested</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Current Step</th>
                  <th className="px-4 py-3">Submitted</th>
                  <th className="px-4 py-3 text-right">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-4 py-8 text-center text-slate-400">
                      {status ? "No requisitions with that status." : "You have no requisitions yet."}
                    </td>
                  </tr>
                )}
                {rows.map((r) => {
                  const reqType = r.requisitionType ?? r.type;
                  return (
                    <tr key={r.id} className="hover:bg-slate-50">
                      <td className="px-4 py-3 font-mono font-semibold text-slate-900">
                        {r.serialNo || `#${r.id}`}
                      </td>
                      <td className="px-4 py-3">
                        <TypeBadge value={reqType} />
                      </td>
                      <td className="px-4 py-3 text-slate-600 max-w-[240px] truncate">
                        <ItemsCell
                          lines={r.lines?.length ? r.lines : linesById[r.id]}
                          pending={!r.lines?.length && !linesById[r.id]}
                        />
                      </td>
                      <td className="px-4 py-3">
                        <StatusBadge value={r.status} />
                      </td>
                      <td className="px-4 py-3 text-slate-500 text-xs">
                        {r.currentStepName ?? "—"}
                      </td>
                      <td className="px-4 py-3 text-slate-500">
                        {r.submittedAt
                          ? new Date(r.submittedAt).toLocaleDateString("en-KE", { day: "numeric", month: "short", year: "numeric" })
                          : "—"}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <Link
                          to={`/hr/requisitions/${r.id}`}
                          className="font-semibold text-emerald-700 hover:underline text-xs"
                        >
                          View
                        </Link>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="flex items-center justify-between text-sm">
            <span className="text-slate-500">
              {data.totalElements.toLocaleString()} total — Page {data.number + 1} of{" "}
              {Math.max(1, data.totalPages)}
            </span>
            <div className="flex gap-2">
              <button
                onClick={() => setPage((p) => p - 1)}
                disabled={data.first}
                className="rounded-lg border px-3 py-1.5 disabled:opacity-40 hover:bg-slate-50"
              >
                Prev
              </button>
              <button
                onClick={() => setPage((p) => p + 1)}
                disabled={data.last}
                className="rounded-lg border px-3 py-1.5 disabled:opacity-40 hover:bg-slate-50"
              >
                Next
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
